import {
    CopilotChat,
    CopilotChatAssistantMessage,
    CopilotChatInput,
    CopilotChatUserMessage,
    useAgent,
    useCopilotKit,
    useDefaultRenderTool,
} from '@copilotkit/react-core/v2'
import { AudienceLanding } from './components/AudienceLanding'
import {
    Container,
    Collapse,
    Card,
    Stack,
    Title,
    Text,
    Loader,
    ActionIcon,
    Modal,
    Group,
} from '@mantine/core'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'
import styles from './ThreadPage.module.css'
import {
    UserMessage,
    AssistantMessage,
    ReasoningHeader,
} from './components/ChatMessages'
import { useChoicePromptTool } from './components/ChoicePrompt'
import { HitlGuardedInput } from './components/HitlGuardedInput'
import { pendingHitlToolCallIds } from './components/hitlCancel'
import { RenderBlocks, EditBooleanCard } from './components/RenderBlocks'
import {
    RecommendedStrategies,
    RecommendedStrategiesPending,
    ADD_TO_CART_MUTATION_KEY,
} from './components/ShowRecommendedStrategiesTool'
import { useBarChartTool } from './components/BarChartTool'
import {
    strategyRecommendationToBlocks,
    audienceConfigToBlocks,
} from './components/renderMappers'
import {
    IconBinoculars,
    IconBulb,
    IconShoppingCartPlus,
    IconShoppingCartX,
    IconShoppingCartCog,
    IconShoppingCartSearch,
    type IconProps,
    IconTool,
    IconShoppingCart,
    IconAdjustmentsPlus,
    IconUsersGroup,
    IconMessageCircle,
    IconCloudSearch,
    IconRestore,
    IconReportAnalytics,
    IconBinary,
} from '@tabler/icons-react'
import { useCallback, useMemo, useState, type FC } from 'react'
import { produce } from 'immer'
import { useMutation } from '@tanstack/react-query'

import { ThreadTitleInput } from './components/ThreadTitleInput'
import { useAgentState, useAutoThreadTitle } from './queries'
import { useThreadService } from './ThreadServiceProvider'
import type { AgentState } from './types'
import {
    DatasetType,
    type AudienceConfiguration,
    type UserAttributesDefinition,
} from '@/lib/domain'
import { datasetIdToName } from '@/lib/datasetNames'
import { SegmentCartItem } from './components/SegmentCartItem'
import {
    SegmentDetail,
    type SegmentStage,
} from '@/features/segment/components/SegmentDetail'
import { useClearInsightsCache } from '@/features/segment/queries'

// Frequent restaurant diners who don't grocery shop. Each TransactionDefinition is
// a pattern over real `credit_card_transactions` columns/values (verified to exist
// in the dev data) so the live insights scan resolves and returns charts — the old
// placeholder `merchant`/`category` fields matched nothing and the panel came back
// empty. `mcc_label` is the transaction category; values are exact MCC labels.
const demoDiningBehavior = {
    description:
        'Frequent restaurant diners who do not shop at grocery stores.',
    must: [
        {
            must: [
                {
                    field: 'mcc_label',
                    values: ['Eating Places and Restaurants'],
                },
            ],
            should: [],
            must_not: [],
        },
    ],
    should: [],
    must_not: [
        {
            must: [
                {
                    field: 'mcc_label',
                    values: ['Grocery Stores, Supermarkets'],
                },
            ],
            should: [],
            must_not: [],
        },
    ],
}

// High-income households that are ALSO a parent household (mom OR dad). The tall
// user-attribute table is one (attribute, value) pair per row, so each group pins
// one attribute by constraining its primary-key columns — `ab_column_name` (which
// attribute) AND `attribute_value` (the value). High income is the required group
// (`must`); the two parent attributes are separate `should` groups (OR across
// groups). Uses real attributes/values from `user_demographics_intent` so the live
// insights query resolves (otherwise the column doesn't exist and the scan errors).
const demoTeenHouseholdAttributes: UserAttributesDefinition = {
    must: [
        {
            conditions: [
                { field: 'ab_column_name', values: ['lu_natlinc'] },
                { field: 'attribute_value', values: ['high'] },
            ],
        },
    ],
    should: [
        {
            conditions: [
                { field: 'ab_column_name', values: ['mom_2child_hh'] },
                { field: 'attribute_value', values: ['yes'] },
            ],
        },
        {
            conditions: [
                { field: 'ab_column_name', values: ['dad_2child_hh'] },
                { field: 'attribute_value', values: ['yes'] },
            ],
        },
    ],
    must_not: [],
}

// A deliberately broad cross — one common condition per side, no exclusions or extra
// requirements — so the intersection stays large enough to clear the insight
// resolution floor and actually render charts. The richer standalone segments above
// intersect to too few users per brand/category slice (every slice falls below the
// floor, so no chart). Restaurant diners (a single MCC) × high income (a single tier).
const demoCrossDiningBehavior = {
    description: 'Frequent restaurant diners.',
    must: [
        {
            must: [
                {
                    field: 'mcc_label',
                    values: ['Eating Places and Restaurants'],
                },
            ],
            should: [],
            must_not: [],
        },
    ],
    should: [],
    must_not: [],
}

const demoHighIncomeAttributes: UserAttributesDefinition = {
    must: [
        {
            conditions: [
                { field: 'ab_column_name', values: ['lu_natlinc'] },
                { field: 'attribute_value', values: ['high'] },
            ],
        },
    ],
    should: [],
    must_not: [],
}

const demoCustomSegments: AudienceConfiguration[] = [
    {
        dataset_type: DatasetType.TRANSACTION,
        dataset_id: 'credit_card_transactions',
        segment_id: 'restaurant_regulars_no_grocery',
        name: 'Restaurant Regulars (No Grocery)',
        description:
            'Frequent restaurant diners who do not shop at grocery stores.',
        audience_size: 15_800_000,
        users_with_transactions: [demoDiningBehavior],
    },
    {
        dataset_type: DatasetType.USER_ATTRIBUTE,
        dataset_id: 'user_demographics_intent',
        segment_id: 'high_income_teen_households',
        name: 'High-Income Teen Households',
        description: 'High-income households with teenage children.',
        audience_size: 20_700_000,
        users_with_attributes: demoTeenHouseholdAttributes,
    },
    {
        dataset_type: DatasetType.CROSSED,
        // The cross spans both datasets: `dataset_id` is the transaction side,
        // `users_with_attributes_dataset_id` the user-attribute side. Uses the broad
        // single-condition defs above (restaurant diners × high income) so the
        // intersection is large enough to render insight charts.
        dataset_id: 'credit_card_transactions',
        users_with_attributes_dataset_id: 'user_demographics_intent',
        users_with_attributes_description: 'High-income households.',
        segment_id: 'crossed_high_income_diners',
        name: 'High-Income Restaurant Diners',
        description:
            'Frequent restaurant diners who are also high-income households.',
        audience_size: 8_400_000,
        users_with_transactions: [demoCrossDiningBehavior],
        users_with_attributes: demoHighIncomeAttributes,
    },
]

const coAgentOptions = {
    // Local registry id of our self-managed ResumableRuntimeAgent (distinct from
    // the runtime agent id so it isn't overwritten by runtime discovery).
    name: 'audience_builder_agent_ui',
}

type ToolMessageText = string | undefined | null
type ToolIconType = React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
>

export interface ThreadPageProps {
    threadId: string
}

export const ThreadPage: FC<ThreadPageProps> = ({ threadId }) => {
    const { agent } = useAgent({ agentId: coAgentOptions.name })
    const { copilotkit } = useCopilotKit()
    const messages = agent.messages
    const { state: agentState, mutateAsync: setAgentStateAsync } =
        useAgentState<AgentState>(threadId, coAgentOptions)
    useAutoThreadTitle(threadId, coAgentOptions)
    const threadService = useThreadService()
    const clearInsightsCache = useClearInsightsCache()

    // Which segment's lifecycle detail modal is open, and at which stage.
    const [detailState, setDetailState] = useState<{
        config: AudienceConfiguration
        stage: SegmentStage
    } | null>(null)

    // Handler for deleting audience configurations
    const handleDeleteConfiguration = useCallback(
        (configIndex: number) => {
            if (!agentState) return

            // Drop the removed segment's cached insights so a stale entry doesn't
            // linger in localStorage.
            const removed = agentState.audienceConfigurations[configIndex]
            if (removed) clearInsightsCache(removed)

            const updatedState = produce(agentState, (draft) => {
                draft.audienceConfigurations.splice(configIndex, 1)
            })

            // Return the patch promise so the cart item's DeleteButton can show
            // pending while it saves and an inline error if it fails.
            return setAgentStateAsync(updatedState)
        },
        [agentState, setAgentStateAsync, clearInsightsCache]
    )

    // Add a recommended strategy's configuration to the cart via the backend,
    // which mirrors the CartManagement tools: it computes a REAL audience size
    // (not the agent's estimate) and upserts by (dataset_id, segment_id),
    // returning the stored — possibly tweaked — config. We mirror that result
    // into local agent state so the cart reflects it immediately, the same
    // pattern as `useUpdateThreadTitle`. Returns the request promise so the
    // strategies tool can surface pending/error on the button; reads
    // `agent.state` at call time, so the callback stays referentially stable for
    // the once-registered tool.
    const handleAddConfiguration = useCallback(
        async (
            config: AudienceConfiguration
        ): Promise<AudienceConfiguration> => {
            const added = await threadService.addConfigurationToCart(
                threadId,
                config
            )
            const prior = (agent.state ?? {}) as Partial<AgentState>
            const next = produce(prior, (draft) => {
                const list = draft.audienceConfigurations ?? []
                const i = list.findIndex(
                    (c) =>
                        c.dataset_id === added.dataset_id &&
                        c.segment_id === added.segment_id
                )
                if (i >= 0) list[i] = added
                else list.push(added)
                draft.audienceConfigurations = list
            })
            agent.setState(next)
            return added
        },
        [threadService, threadId, agent]
    )

    // "Refine" a recommended strategy: hand its config to the matching segment
    // agent to edit/finish before it lands in the cart. We (1) set the editing
    // state to the config (the slot the segment agent reads) and (2) fabricate
    // the `transfer_to_agent` tool call + result the backend would emit, then run
    // — so the run resumes inside that segment agent. Reads agent.state at call
    // time (not the agentState closure), so the callback stays referentially
    // stable for the once-registered strategies tool. Crossed configs have no
    // editing slot, so they're not refinable (the button isn't shown for them).
    const handleRefineConfiguration = useCallback(
        (config: AudienceConfiguration) => {
            const prior = (agent.state ?? {}) as AgentState
            let next: AgentState
            if (config.dataset_type === DatasetType.TRANSACTION) {
                next = { ...prior, editingTransactionAudience: config }
            } else if (config.dataset_type === DatasetType.USER_ATTRIBUTE) {
                next = { ...prior, editingUserAttributeAudience: config }
            } else {
                return
            }
            agent.setState(next)
            // Load the config into the editing state (the slot the segment agent
            // reads), then drive the run with a plain user turn. We do NOT
            // fabricate a transfer_to_agent: the ag-ui-adk middleware only honors
            // a tool result that answers a PENDING client tool call, so an
            // injected transfer result is skipped ("no matching pending tool
            // calls") and — worse — the skipped tool-result batch takes the user
            // message with it, so the run ends with no terminal event. A plain
            // user turn is classified as a normal message and reliably drives the
            // active agent, which refines using the editing state (and transfers
            // to the segment agent itself as needed).
            const label = config.name || config.segment_id
            agent.addMessage({
                id: crypto.randomUUID(),
                role: 'user',
                content: `Let's refine the "${label}" segment.`,
            })
            void copilotkit.runAgent({ agent })
        },
        [agent, copilotkit]
    )

    // Registers the `ask_user_question` HITL widget (multiple-choice / write-in)
    // so the agent can ask structured questions instead of free text.
    useChoicePromptTool()

    // `show_recommended_strategies` is now a SERVER tool (it sizes each
    // configuration server-side and returns the sized strategies); we render it
    // from the tool result in useDefaultRenderTool below. This add-to-cart
    // mutation is keyed so each card reads its own pending/error status from the
    // mutation cache (see RecommendedStrategies / useMutationState).
    const addToCart = useMutation({
        mutationKey: ADD_TO_CART_MUTATION_KEY,
        mutationFn: handleAddConfiguration,
    })

    // Display-only `show_bar_chart` tool: the agent draws a horizontal bar chart
    // to compare values instead of a table/prose.
    useBarChartTool()

    useDefaultRenderTool({
        render: ({ name, parameters, status, result }) => {
            const args = (parameters ?? {}) as Record<string, unknown>

            // `show_recommended_strategies` is a server tool that validates and
            // sizes each configuration, then returns them as its result. Render
            // ONLY the completed result — never the streaming args: the args may
            // be invalid/unnamed mid-stream, and a retried tool call (new
            // toolCallId) would render a duplicate card. While the tool runs, show
            // a compact progress note.
            if (name === 'show_recommended_strategies') {
                if (status !== 'complete' || typeof result !== 'string') {
                    return <RecommendedStrategiesPending />
                }
                let parsed: Record<string, unknown>
                try {
                    parsed = JSON.parse(result) as Record<string, unknown>
                    // ADK wraps a tool's string return as `{"result": "<json>"}`;
                    // unwrap one level to reach `{summary, strategies}`.
                    if (typeof parsed.result === 'string') {
                        parsed = JSON.parse(parsed.result) as Record<
                            string,
                            unknown
                        >
                    }
                } catch {
                    return <></>
                }
                if (!Array.isArray(parsed.strategies)) return <></>
                return (
                    <RecommendedStrategies
                        args={parsed}
                        names={datasetIdToName}
                        onAddToCart={addToCart.mutate}
                        onRefine={handleRefineConfiguration}
                        agentId={coAgentOptions.name}
                    />
                )
            }

            // HYBRID auto-render: flash-lite reliably calls these side-effect
            // tools (carrying the full structured payload) but skips the
            // display-only render tool — so we render those calls as block cards
            // ourselves, mapping each tool's args into blocks.
            if (
                name === 'add_strategy_recommendation' ||
                name === 'update_strategy_recommendation'
            ) {
                const rec = (args.recommendation ?? {}) as Record<
                    string,
                    unknown
                >
                return (
                    <RenderBlocks
                        args={strategyRecommendationToBlocks(
                            rec,
                            datasetIdToName
                        )}
                    />
                )
            }
            if (name === 'save_transaction_audience') {
                const cfg = (args.audience_config ?? {}) as Record<
                    string,
                    unknown
                >
                return (
                    <RenderBlocks
                        args={audienceConfigToBlocks(cfg, datasetIdToName)}
                    />
                )
            }
            if (name === 'edit_new_boolean_logic_audience') {
                // Rendered as a child component (not inline) so it can subscribe
                // to agent state and re-render itself when combine writes the
                // size — see EditBooleanCard.
                return <EditBooleanCard args={args} />
            }

            let message: ToolMessageText = undefined
            let ToolIcon: ToolIconType = IconTool

            // transfer_to_agent tool
            if (name === 'transfer_to_agent') {
                const nameToMessage: Record<string, ToolMessageText> = {
                    AudienceBuilderCoordinator: null,
                    StrategyAgent: 'Analyzing request and selecting datasets',
                    SegmentCreationAgent: null,
                    TransactionSegmentAgent:
                        'Preparing to build transaction audience',
                    UserAttributeSegmentAgent:
                        'Finding user attribute segments',
                }
                const agentName = args['agent_name'] as string
                message = nameToMessage[agentName]
                if (message === undefined) {
                    message = 'Working...'
                }
                ToolIcon = IconBulb
            }

            // cart tools
            const cartToolsToMessage: Record<string, ToolMessageText> = {
                add_user_attribute_segment_to_cart:
                    'Adding user attribute segment to cart',
                add_transaction_segment_to_cart:
                    'Adding transaction segment to cart',
                add_crossed_segment_to_cart: 'Adding crossed segment to cart',
                edit_transaction_segment: 'Editing transaction segment',
                discard_edited_segment: 'Discarding changes',
                remove_segment_from_cart: 'Removing segment from cart',
                get_segments_in_cart: 'Retrieving cart contents',
            }
            const cartToolsToIcon: Record<string, ToolIconType> = {
                add_user_attribute_segment_to_cart: IconShoppingCartPlus,
                add_transaction_segment_to_cart: IconShoppingCartPlus,
                add_crossed_segment_to_cart: IconShoppingCartPlus,
                edit_transaction_segment: IconShoppingCartCog,
                discard_edited_segment: IconShoppingCartX,
                remove_segment_from_cart: IconShoppingCartX,
                get_segments_in_cart: IconShoppingCartSearch,
            }
            if (Object.keys(cartToolsToMessage).includes(name)) {
                message = cartToolsToMessage[name]
                ToolIcon = cartToolsToIcon[name] ?? IconShoppingCart
            }

            // dataset strategy tools
            const datasetStrategyTools = [
                'search_transaction_dataset_tool',
                'search_user_attribute_dataset_tool',
            ]
            if (datasetStrategyTools.includes(name)) {
                const datasetId = args['dataset_id'] as string
                const datasetName = datasetIdToName[datasetId]

                if (datasetName !== undefined) {
                    message =
                        name === 'search_transaction_dataset_tool'
                            ? `Searching ${datasetName} signals data`
                            : `Searching ${datasetName} user attribute segments`
                } else {
                    message =
                        name === 'search_transaction_dataset_tool'
                            ? `Searching transaction dataset`
                            : `Searching user attribute segments`
                }
                ToolIcon = IconBinoculars
            }

            // custom audience tools
            const customAudienceToolsToMessage: Record<
                string,
                ToolMessageText
            > = {
                edit_new_audience_definition: 'Creating transaction segment',
                save_transaction_audience: 'Updating transaction audience',
            }
            const customAudienceToolsToIcon: Record<string, ToolIconType> = {
                save_transaction_audience: IconAdjustmentsPlus,
                edit_new_audience_definition: IconAdjustmentsPlus,
            }
            if (Object.keys(customAudienceToolsToMessage).includes(name)) {
                message = customAudienceToolsToMessage[name]
                ToolIcon = customAudienceToolsToIcon[name]
            }

            // standard audience tools
            const standardAudienceToolsToMessage: Record<
                string,
                ToolMessageText
            > = {
                edit_new_boolean_logic_audience: 'Updating audience selection',
                combine_segments_tool: 'Applying logic',
            }
            const standardAudienceToolsToIcon: Record<string, ToolIconType> = {
                edit_new_boolean_logic_audience: IconAdjustmentsPlus,
                combine_segments_tool: IconBinary,
            }
            if (Object.keys(standardAudienceToolsToMessage).includes(name)) {
                message = standardAudienceToolsToMessage[name]
                ToolIcon = standardAudienceToolsToIcon[name]
            }

            // external data tools
            if (name === 'entity_fact_finder') {
                message = 'Searching external datasets'
                ToolIcon = IconCloudSearch
            }

            // Display toolcall
            if (message !== undefined && message !== null) {
                return (
                    <Group
                        gap='xs'
                        wrap='nowrap'
                        align='center'
                        style={{
                            color: 'var(--mantine-color-gray-7)',
                            fontSize: 'var(--mantine-font-size-sm)',
                        }}
                    >
                        <Loader
                            size={14}
                            style={{
                                visibility:
                                    status !== 'complete'
                                        ? 'visible'
                                        : 'hidden',
                            }}
                        />
                        <ToolIcon
                            size={16}
                            style={{
                                color: 'var(--mantine-color-grape-6)',
                            }}
                        />
                        <span>{message}</span>
                    </Group>
                )
            }

            // Purposefully hide toolcall
            if (message === null) {
                return <></>
            }

            // Unhandled toolcall
            return (
                <Group
                    gap='xs'
                    wrap='nowrap'
                    align='center'
                    style={{
                        color: 'var(--mantine-color-gray-7)',
                        fontSize: '.85rem',
                    }}
                >
                    <Loader
                        size={14}
                        style={{
                            visibility:
                                status !== 'complete' ? 'visible' : 'hidden',
                        }}
                    />
                    <IconBulb
                        size={16}
                        style={{
                            color: 'var(--mantine-color-grape-6)',
                        }}
                    />
                    <span>Working...</span>
                </Group>
            )
        },
    })

    const handleStartAudience = useCallback(
        (description: string, title: string) => {
            // v2's cloneForThread initializes agent.state to `{}`, which is
            // truthy but has none of AgentState's required fields. Normalize
            // the same way useAgentState does — treat null / undefined /
            // empty object as "no state yet" — and always write a complete
            // AgentState so downstream code can rely on arrays being
            // present.
            const rawState = agent.state as Partial<AgentState> | null
            const hasInitializedState =
                rawState != null &&
                typeof rawState === 'object' &&
                Object.keys(rawState).length > 0
            const prior: Partial<AgentState> = hasInitializedState
                ? rawState
                : {}
            agent.setState({
                audienceConfigurations: prior.audienceConfigurations ?? [],
                datasets: prior.datasets ?? [],
                ...prior,
                threadTitle: title,
            })
            agent.addMessage({
                id: crypto.randomUUID(),
                role: 'user',
                content: description,
            })
            void copilotkit.runAgent({ agent })
        },
        [agent, copilotkit]
    )

    // Don't memo on `messages` — the v2 agent mutates `agent.messages` in
    // place on `addMessage`, so reference stability can hide new messages
    // from the memo. `messages.length > 0` is zero-cost anyway.
    const hasMessages = messages.length > 0

    // While an ask_user_question (HITL) call is awaiting an answer, sending a
    // message dismisses it and asks something else (see HitlGuardedInput) —
    // hint that in the placeholder.
    const hitlPending = pendingHitlToolCallIds(messages).length > 0

    const copilotLabels = useMemo(() => {
        return {
            chatInputPlaceholder: hitlPending
                ? 'Answer above, or type to ask something else...'
                : !hasMessages
                  ? 'Example: Tech-savvy millennials interested in sustainable living and eco-friendly products...'
                  : 'Build your audience...',
        }
    }, [hasMessages, hitlPending])

    const agentAudienceConfigs = useMemo(
        () => agentState?.audienceConfigurations ?? [],
        [agentState?.audienceConfigurations]
    )

    const handleAddDefaultsToCart = useCallback(() => {
        if (!agentState) return

        const updatedState = produce(agentState, (draft) => {
            draft.audienceConfigurations.push(...demoCustomSegments)
        })

        void setAgentStateAsync(updatedState)
    }, [agentState, setAgentStateAsync])

    return (
        <>
            <Modal
                opened={detailState !== null}
                onClose={() => setDetailState(null)}
                size='90%'
                title={
                    <Text size='xl' fw='bold'>
                        <IconReportAnalytics
                            style={{ verticalAlign: 'text-bottom' }}
                        />{' '}
                        {detailState?.config.name || 'Segment details'}
                    </Text>
                }
                overlayProps={{
                    backgroundOpacity: 0.55,
                    blur: 3,
                }}
            >
                {detailState && (
                    <SegmentDetail
                        config={detailState.config}
                        threadId={threadId}
                        names={datasetIdToName}
                        datasets={agentState?.datasets ?? []}
                        initialStage={detailState.stage}
                        onClose={() => setDetailState(null)}
                    />
                )}
            </Modal>

            <PanelGroup
                autoSaveId='knowledge_graph_panel_group'
                direction='horizontal'
            >
                <Panel
                    defaultSize={75}
                    id='main'
                    order={1}
                    style={{ overflow: 'auto', height: '100%' }}
                >
                    <Container
                        size='lg'
                        h='100%'
                        style={{ display: 'flex', flexDirection: 'column' }}
                    >
                        <Collapse in={!hasMessages} transitionDuration={750}>
                            <AudienceLanding
                                onStartAudience={handleStartAudience}
                            />
                        </Collapse>
                        {agentState && (
                            <ThreadTitleInput
                                key={agentState.threadTitle}
                                title={
                                    agentState.threadTitle === null
                                        ? undefined
                                        : agentState.threadTitle
                                }
                                onSave={(newTitle) =>
                                    setAgentStateAsync({
                                        ...agentState,
                                        threadTitle: newTitle,
                                    })
                                }
                                placeholder='Enter thread title...'
                            />
                        )}

                        <div
                            style={{
                                minHeight: '200px',
                                flexDirection: 'row',
                                flex: '1 auto',
                            }}
                        >
                            <CopilotChat
                                className={styles['my-chat']}
                                agentId={coAgentOptions.name}
                                threadId={threadId}
                                labels={copilotLabels}
                                messageView={{
                                    // Our wrappers satisfy the runtime
                                    // contract (same props, same subcomponent
                                    // expectations) but don't carry the
                                    // static sub-slot namespace that v2's
                                    // SlotValue<C> type demands. Cast through
                                    // unknown to silence the type without
                                    // forging those statics.
                                    userMessage:
                                        UserMessage as unknown as typeof CopilotChatUserMessage,
                                    assistantMessage:
                                        AssistantMessage as unknown as typeof CopilotChatAssistantMessage,
                                    // Keep the reasoning status header
                                    // ("Thinking…" / "Thought for Xs", sans
                                    // chevron) but render no content, so the
                                    // agent's raw chain-of-thought tokens
                                    // never surface in the chat.
                                    reasoningMessage: {
                                        header: ReasoningHeader,
                                        contentView: () => null,
                                    },
                                }}
                                input={
                                    // Custom input slot: while a HITL choice is
                                    // pending, a typed message answers it
                                    // instead of stranding the call. It carries
                                    // our send-button icon. Cast for the same
                                    // reason as messageView below — our
                                    // component lacks v2's slot statics.
                                    HitlGuardedInput as unknown as typeof CopilotChatInput
                                }
                                onError={(event) =>
                                    console.error('Chat error:', event)
                                }
                            />
                        </div>
                    </Container>
                </Panel>
                <PanelResizeHandle
                    style={{
                        borderRight: '1px solid var(--app-shell-border-color)',
                        width: '7px',
                    }}
                />
                <Panel id='review' order={2} style={{ overflow: 'auto' }}>
                    <Container size='sm' pt={'md'}>
                        <Collapse in={hasMessages}>
                            <Title order={3} mb='md' c='gray.7'>
                                <IconUsersGroup
                                    size={32}
                                    style={{ verticalAlign: 'text-bottom' }}
                                />{' '}
                                Your Segments
                            </Title>
                            <Stack
                                pr='sm'
                                style={{
                                    height: 'calc(100vh - 200px)',
                                    overflow: 'auto',
                                }}
                            >
                                {agentAudienceConfigs.length === 0 && (
                                    <>
                                        <Card
                                            padding='lg'
                                            radius='md'
                                            withBorder
                                            mt='md'
                                        >
                                            <Stack
                                                gap='xl'
                                                align='center'
                                                py='xl'
                                            >
                                                <IconShoppingCart
                                                    color='var(--mantine-color-blue-2)'
                                                    size={48}
                                                />
                                                <Text
                                                    c='gray.6'
                                                    style={{
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Ready to build your
                                                    audience?
                                                </Text>
                                                <IconMessageCircle
                                                    color='var(--mantine-color-blue-2)'
                                                    size={48}
                                                />
                                                <Text
                                                    c='gray.6'
                                                    style={{
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Ask the agent to add
                                                    segments you build or find
                                                    to your cart.
                                                </Text>
                                            </Stack>
                                        </Card>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <ActionIcon
                                                variant='subtle'
                                                aria-label='Add default segments to cart'
                                                onClick={
                                                    handleAddDefaultsToCart
                                                }
                                            >
                                                <IconRestore size={16} />
                                            </ActionIcon>
                                        </div>
                                    </>
                                )}
                                {agentAudienceConfigs.map((config, index) => {
                                    const uniqueKey = `${config.dataset_type}-${config.segment_id}-${config.dataset_id}`
                                    return (
                                        <SegmentCartItem
                                            key={uniqueKey}
                                            config={config}
                                            threadId={threadId}
                                            names={datasetIdToName}
                                            onDelete={() =>
                                                handleDeleteConfiguration(index)
                                            }
                                            onOpenDetail={(c, stage) =>
                                                setDetailState({
                                                    config: c,
                                                    stage,
                                                })
                                            }
                                        />
                                    )
                                })}
                            </Stack>
                        </Collapse>
                    </Container>
                </Panel>
            </PanelGroup>
        </>
    )
}
